import { ReactNode, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { CCMSProvider, AuthProvider, useHttpClient } from '@actschurch/ccms-sdk';
import { BrowserRouter } from 'react-router-dom';
import { theme, cssVariablesResolver } from '@/theme/theme';
import { proceduresColorSchemeManager } from '@/theme/colorScheme';
import { CCMS_BASE_URL, CCMS_SERVICE_PATH_PROCEDURES } from '@/config/ccms';
import { createSecureStorage } from '@/utils/secureStorage';
import { SyncEngine } from '@/components/sync/SyncEngine';

/** Appearance choice (Match my phone / Light / Dark), persisted in localStorage inside try/catch. */
const colorSchemeManager = proceduresColorSchemeManager();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
      staleTime: 60 * 1000,
      // Plant rooms have weak signal: keep showing what we have instead of pausing on "offline".
      networkMode: 'always',
    },
    mutations: { networkMode: 'always' },
  },
});

/**
 * Wraps the SDK <c>AuthProvider</c>. Has to live inside <c>CCMSProvider</c> so it can read the
 * <c>HttpClient</c>. Same pattern as ccms_v2, the Resource Centre and Carpool.
 */
function AppWithAuth({ children }: { children: ReactNode }) {
  const httpClient = useHttpClient();
  const storage = useMemo(() => createSecureStorage(), []);
  return (
    <AuthProvider
      httpClient={httpClient}
      storage={storage}
      autoRefresh
      refreshThresholdSeconds={60}
      onError={(err) => console.error('[Auth] error', err)}
    >
      {children}
    </AuthProvider>
  );
}

/**
 * Root providers for the Procedures portal.
 * - <c>CCMSProvider</c> wires the HttpClient + the SDK services we use: users (sign in, Planning
 *   Center), members (profile) and procedures (everything else: public + member portal services).
 * - <c>AppWithAuth</c> wires the SDK's <c>AuthProvider</c> + secure storage.
 * - <c>SyncEngine</c> sends the offline queue of step ticks whenever there is signal.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <CCMSProvider
          config={{ baseUrl: CCMS_BASE_URL }}
          services={{
            users: { servicePath: 'users' },
            members: { servicePath: 'members' },
            procedures: { servicePath: CCMS_SERVICE_PATH_PROCEDURES },
          }}
        >
          <AppWithAuth>
            <SyncEngine />
            <MantineProvider theme={theme} defaultColorScheme="auto" colorSchemeManager={colorSchemeManager} cssVariablesResolver={cssVariablesResolver}>
              <Notifications position="top-center" zIndex={400} />
              <ModalsProvider>{children}</ModalsProvider>
            </MantineProvider>
          </AppWithAuth>
        </CCMSProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
